import { isEmpty, isNil } from 'lodash';
import { Dispatch, SetStateAction, useContext } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { ButtonView, ScrollView, View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name, MBImage } from '@shared/components/image';
import { LoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import styles from './SelectableItemList.module.css';

export interface ListItem {
  id: string;
  label: string;
  labelWithCount?: string;
  icon?: MBIconV2Name;
  imageUrl?: string;
}

interface SelectableItemListProps {
  selectedItem?: string;
  items: ListItem[];
  onSelectItem: (id: string) => void;
  enabled: boolean;
  query?: string;
  setQuery?: Dispatch<SetStateAction<string>>;
  queryPlaceholder?: string;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  helpText?: string;
  label?: string;
  showRadios?: boolean;
}

export function SelectableItemList({
  selectedItem,
  items,
  onSelectItem,
  enabled,
  query,
  setQuery,
  queryPlaceholder,
  loading = false,
  hasMore = false,
  loadMore,
  helpText,
  label,
  showRadios = false,
}: SelectableItemListProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <View>
      {!isNil(label) && (
        <View paddingHorizontal={2} paddingVertical={1}>
          <Text size="overline" weight="semibold" inactive>
            {label}
          </Text>
        </View>
      )}
      {!isNil(setQuery) && (
        <View paddingVertical={1} paddingHorizontal={2}>
          <MBSearchBar
            value={query}
            onChangeText={setQuery}
            placeholder={queryPlaceholder ?? i18n('common.search')}
          />
        </View>
      )}
      {!isNil(helpText) && (
        <View paddingHorizontal={2} paddingVertical={1}>
          <Text size="body2" inactive>
            {helpText}
          </Text>
        </View>
      )}
      {loading && (
        <View center style={{ height: 150 }}>
          <LoadingIndicator />
        </View>
      )}
      {!loading && isEmpty(items) && (
        <View paddingBottom={1} centerHorizontally paddingHorizontal={2}>
          <View center padding={1}>
            <MBIconV2 name="list" color={colors.accent} size={grid(7)} />
          </View>
          <Text size="body1" align="center">
            {i18n('common.empty')}
          </Text>
        </View>
      )}
      <ScrollView>
        {items.map(item => {
          return (
            <ButtonView
              key={item.id}
              fullWidth
              className={[styles.row, enabled && styles.enabled]}
              onClick={() => onSelectItem(item.id)}
            >
              <View row centerVertically paddingHorizontal={1.5} style={{ height: 40 }}>
                {showRadios && (
                  <View marginRight={2}>
                    <MBIconV2
                      name={item.id === selectedItem ? 'check-filled' : 'circle'}
                      color={Colors.v2.green}
                      size={grid(5)}
                    />
                  </View>
                )}
                {!showRadios && !isNil(item.icon) && (
                  <View paddingRight={1}>
                    <MBIconV2 name={item.icon} color={colors.foregroundActive} size={grid(3)} />
                  </View>
                )}
                {isNil(item.icon) && !isNil(item.imageUrl) && (
                  <MBImage
                    source={{ uri: item.imageUrl }}
                    style={{ height: grid(3), width: grid(3) }}
                  />
                )}
                <View fill centerVertically paddingLeft={1}>
                  <Text size="body1">{item.label}</Text>
                </View>
                {!showRadios && item.id === selectedItem && (
                  <MBIconV2 name="check-filled" size={grid(4)} color={colors.accent} />
                )}
              </View>
            </ButtonView>
          );
        })}
        {hasMore && (
          <View center paddingVertical={2}>
            <Button
              variant="tertiary"
              size="medium"
              label={i18n('card.list.showMore')}
              onClick={loadMore}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
}
